<template>
    <div class="flow">
        <header class="flow-header">
            <SegmentedProgress v-if="progressSegment !== null" :total="TOTAL_SEGMENTS" :current="progressSegment" />
        </header>

        <!-- Keep the status region mounted across steps so automatic transitions do not remove pending
             announcements.
             -->
        <output class="sr-only" aria-label="Onboarding status">{{ screenStatus }}</output>

        <div ref="flowBody" class="flow-body" tabindex="-1" aria-label="Onboarding step">
            <OnboardingErrorView v-if="apiError" @retry="handleRetry" />

            <IdentityStep v-else-if="step === 'identity'" :prefill="run.identity_prefill" :busy="busy" @submit="handleIdentity" />

            <IntroStep v-else-if="step === 'intro'" :first-name="firstName" :manages-people="managesPeople" @complete="handleAdvance('intro')" />

            <ContextRingsStep v-else-if="step === 'context_rings'" :context="run.context" @continue="handleAdvance('context_rings')" />

            <CalendarConnectStep
                v-else-if="step === 'calendar_connect'"
                :calendar="run.calendar"
                :organization-name="run.organization_name"
                :busy="busy"
                @connect="connectCalendar"
                @skip="handleSkipCalendar" />

            <ThinkingScreen
                v-else-if="step === 'scanning'"
                v-bind="THINKING_SCREENS.scan"
                :label="scanLabel"
                :show-continue="scanPhase.outcome.value === 'timed_out' || scanPhase.outcome.value === 'error'"
                @continue="handleAdvance('scanning')" />

            <CalendarPayoffStep
                v-else-if="step === 'calendar_payoff'"
                :stats="scanStats"
                :readings="scanReadings"
                :readings-settled="readingsSettled"
                @continue="handleAdvance('calendar_payoff')" />

            <AssessmentIntroStep
                v-else-if="step === 'assessment_intro'"
                :stats="scanStats"
                :manages-people="managesPeople"
                :question-count="run.assessment_question_count"
                :calendar-skipped="run.calendar_skipped"
                @continue="handleAdvance('assessment_intro')"
                @skip="handleSkipAssessment" />

            <AssessmentStep v-else-if="step === 'assessment'" :assessment="assessment" :busy="busy" @submitted="handleAssessmentComplete" @skip="handleSkipAssessment" />

            <ThinkingScreen v-else-if="step === 'perspective_result' && perspectiveResult === null" v-bind="THINKING_SCREENS.perspective" />

            <PerspectiveResultStep
                v-else-if="step === 'perspective_result' && perspectiveResult !== null"
                :result="perspectiveResult"
                @continue="handleAdvance('perspective_result')" />

            <!-- The first reading to land ends the thinking screen; the readings
                 step fills the remaining slots in place as the rest arrive. -->
            <ThinkingScreen
                v-else-if="step === 'sharpened' && !sharpenedSettled && !hasSharpenedReadings"
                v-bind="THINKING_SCREENS.readings"
                :show-continue="sharpenedPhase.outcome.value === 'timed_out' || sharpenedPhase.outcome.value === 'error'"
                @continue="handleAdvance('sharpened')" />

            <SharpenedReadingsStep
                v-else-if="step === 'sharpened'"
                :readings="sharpenedReadings"
                :settled="sharpenedSettled"
                :available-access-levels="run.available_access_levels"
                @continue="handleAdvance('sharpened')" />

            <ProfileIntroStep
                v-else-if="step === 'profile_intro'"
                :first-name="run.identity_prefill.first_name"
                :manages-people="managesPeople"
                :busy="busy"
                @continue="handleAdvance('profile_intro')"
                @skip="handleComplete" />

            <!-- Nothing was generated and nothing will be, so there is no document to
                 show and no publishing choice to ask for -- `enterStep` is already
                 finishing the run behind this. -->
            <ThinkingScreen v-else-if="step === 'profile' && profileSkipped" label="Taking you to Nadia…" />

            <ThinkingScreen
                v-else-if="step === 'profile' && profileGenerating"
                v-bind="THINKING_SCREENS.profile"
                :show-continue="profilePhase.outcome.value === 'timed_out'"
                @continue="handleComplete" />

            <ProfilePublishStep
                v-else-if="step === 'profile'"
                :preview="profilePreview"
                :available-access-levels="run.available_access_levels"
                :profile-sharer-names="scanStats?.profile_sharer_names ?? []"
                :generation-failed="profilePreview.status === 'failed'"
                :busy="busy"
                @publish="handlePublish"
                @skip="handleComplete" />

            <ThinkingScreen v-else label="Taking you to Nadia" />
        </div>

        <OnboardingFooter @open-privacy="privacyOpen = true" />
        <PrivacyNoticeDialog v-model:open="privacyOpen" />
        <DevToolsLauncher />
    </div>
</template>

<script setup lang="ts">
import AssessmentIntroStep from "~vue/CalendarFirstOnboarding/AssessmentIntroStep.vue";
import AssessmentStep from "~vue/CalendarFirstOnboarding/AssessmentStep.vue";
import CalendarConnectStep from "~vue/CalendarFirstOnboarding/CalendarConnectStep.vue";
import CalendarPayoffStep from "~vue/CalendarFirstOnboarding/CalendarPayoffStep.vue";
import ContextRingsStep from "~vue/CalendarFirstOnboarding/ContextRingsStep.vue";
import IdentityStep from "~vue/CalendarFirstOnboarding/IdentityStep.vue";
import IntroStep from "~vue/CalendarFirstOnboarding/IntroStep.vue";
import OnboardingErrorView from "~vue/CalendarFirstOnboarding/OnboardingErrorView.vue";
import OnboardingFooter from "~vue/CalendarFirstOnboarding/OnboardingFooter.vue";
import PerspectiveResultStep from "~vue/CalendarFirstOnboarding/PerspectiveResultStep.vue";
import ProfileIntroStep from "~vue/CalendarFirstOnboarding/ProfileIntroStep.vue";
import ProfilePublishStep from "~vue/CalendarFirstOnboarding/ProfilePublishStep.vue";
import SegmentedProgress from "~vue/CalendarFirstOnboarding/SegmentedProgress.vue";
import SharpenedReadingsStep from "~vue/CalendarFirstOnboarding/SharpenedReadingsStep.vue";
import ThinkingScreen from "~vue/CalendarFirstOnboarding/ThinkingScreen.vue";
import { THINKING_SCREENS } from "~vue/CalendarFirstOnboarding/thinkingScreens";
import type { AccessLevel, OnboardingStep, RunState } from "~vue/CalendarFirstOnboarding/types";
import { STEP_ORDER, stepIndex } from "~vue/CalendarFirstOnboarding/types";
import { type IdentitySubmission, useOnboardingRun } from "~vue/CalendarFirstOnboarding/useOnboardingRun";
import { useStepHistory } from "~vue/CalendarFirstOnboarding/useStepHistory";
import DevToolsLauncher from "~vue/DevTools/DevToolsLauncher.vue";
import PrivacyNoticeDialog from "~vue/Onboarding/PrivacyNoticeDialog.vue";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

/**
 * Two positions, not one. `run.current_step` is the cursor -- how far the user
 * has got, owned by the server because the flow leaves the page for calendar
 * OAuth. `viewStep` is what is on screen, owned by the URL so that the browser's
 * own back and forward buttons work the way they do everywhere else.
 *
 * They are usually the same. They differ whenever the user has navigated back:
 * the screen rewinds, the progress never does. A screen behind the cursor still
 * saves what it collects -- `set_step` on the server refuses to move the cursor
 * backwards -- but its Continue button only moves the view, because the
 * transition it would have posted already happened.
 * Keep server interactions in useOnboardingRun so other hosts can reuse the steps; this page owns linear
 * navigation.
 */
const props = defineProps<{ run: RunState; view_step: OnboardingStep }>();

const TOTAL_SEGMENTS = 6;

/**
 * A payoff screen shares its segment with the step that produced it — those
 * screens are that step's result, not a new ask. Publishing gets a segment of
 * its own because it *is* a new ask. `intro` has none: it is Nadia talking, and
 * a progress bar there implies the user is filling something in.
 */
const PROGRESS: Partial<Record<OnboardingStep, number>> = {
    identity: 1,
    intro: 2,
    context_rings: 2,
    calendar_connect: 3,
    scanning: 3,
    calendar_payoff: 3,
    assessment_intro: 4,
    assessment: 4,
    perspective_result: 4,
    sharpened: 5,
    profile_intro: 6,
    profile: 6,
};

const {
    run,
    busy,
    apiError,
    assessment,
    profilePreview,
    scanPhase,
    sharpenedPhase,
    profilePhase,
    managesPeople,
    firstName,
    perspectiveResult,
    scanStats,
    scanReadings,
    sharpenedReadings,
    readingsSettled,
    sharpenedSettled,
    hasSharpenedReadings,
    profileGenerating,
    profileSkipped,
    scanLabel,
    enterStep,
    submitIdentity,
    connectCalendar,
    skipCalendar,
    completeAssessment,
    skipAssessment,
    publishProfile,
    advance,
    complete,
} = useOnboardingRun(props.run);

const step = ref<OnboardingStep>(props.view_step);
const privacyOpen = ref(false);

const cursor = computed(() => run.value.current_step);
const isBehindCursor = computed(() => stepIndex(step.value) < stepIndex(cursor.value));
const progressSegment = computed(() => PROGRESS[step.value] ?? null);

/** The step after this one, or null at the end of the flow. */
function nextStep(from: OnboardingStep): OnboardingStep | null {
    return STEP_ORDER[stepIndex(from) + 1] ?? null;
}

/**
 * Mirrors `resolve_view_step` on the server, for back and forward presses that
 * never reach it. Anything unshowable resolves forward rather than dead-ending.
 */
function resolveViewStep(requested: string | null): OnboardingStep {
    const candidate = requested as OnboardingStep | null;
    if (candidate === null || stepIndex(candidate) < 0 || stepIndex(candidate) > stepIndex(cursor.value)) {
        return cursor.value;
    }
    /* Both calendar-result screens describe a scan that never ran for a skipper. */
    if (run.value.calendar_skipped && (candidate === "scanning" || candidate === "calendar_payoff")) {
        return "assessment_intro";
    }
    if (candidate === "scanning" && cursor.value !== "scanning") {
        return "calendar_payoff";
    }
    /* The skip jumped this whole branch: no session to reopen, no result to name,
       and no second read of the calendar to show. */
    if (run.value.assessment_skipped && (candidate === "assessment" || candidate === "perspective_result" || candidate === "sharpened")) {
        return "profile";
    }
    /* Re-entering a finished assessment would open a fresh session against a
       completed one, so the back button lands on its result instead. Only once
       the cursor has left it: a user who arrived with a result from some earlier
       assessment has still not taken this one. */
    if (candidate === "assessment" && stepIndex(cursor.value) > stepIndex("assessment") && run.value.has_perspective_result) {
        return "perspective_result";
    }
    return candidate;
}

const { navigate } = useStepHistory({
    step,
    resolve: resolveViewStep,
    onPop: () => {
        apiError.value = false;
    },
});

/*
 * Announce step names because multiple steps share a progress segment; the progress bar already
 * communicates position.
 */
const SCREEN_LABELS: Record<OnboardingStep, string> = {
    identity: "About you",
    intro: "Meet Nadia",
    context_rings: "What I can learn about you",
    calendar_connect: "Connect your calendar",
    scanning: "Reading your calendar",
    calendar_payoff: "What I found in your calendar",
    assessment_intro: "About the Perspectives questions",
    assessment: "Perspectives questions",
    perspective_result: "Your Perspectives archetype",
    sharpened: "Your readings",
    profile_intro: "About your collaboration profile",
    profile: "Your collaboration profile",
    done: "Taking you to Nadia",
};

const screenStatus = computed(() => (apiError.value ? "Something went wrong" : SCREEN_LABELS[step.value]));

/**
 * Move the view on after acting on a step. When the server moved the cursor,
 * follow it -- it owns non-linear jumps like skipping the calendar. When it did
 * not, the user was revisiting a step behind the cursor, so just step forward.
 */
function advanceViewAfter(actedStep: OnboardingStep, cursorBefore: OnboardingStep, { replace = false }: { replace?: boolean } = {}): void {
    if (cursor.value !== cursorBefore) {
        navigate(cursor.value, { replace });
        return;
    }
    const next = nextStep(actedStep);
    if (next !== null) {
        navigate(next, { replace });
    }
}

/* Always posted, even from behind the cursor: someone who went back to correct
   their name expects the correction saved. The cursor does not move backwards. */
async function handleIdentity(payload: IdentitySubmission): Promise<void> {
    const cursorBefore = cursor.value;
    if (!(await submitIdentity(payload))) {
        return;
    }
    advanceViewAfter("identity", cursorBefore);
}

/**
 * Move the view on without posting, for a screen the cursor has already left.
 *
 * The transition its button would post is the one that moved the cursor past it
 * in the first place, and the endpoints that start work refuse to run it twice
 * -- they answer 409, which the flow can only recover from by reloading. So a
 * revisited screen's Continue and Skip move the view and nothing else. Answers
 * a screen *collects* are a different matter: those still post, which is how
 * going back to correct your name works.
 */
function advanceViewOnly(actedStep: OnboardingStep, { replace = false }: { replace?: boolean } = {}): boolean {
    if (!isBehindCursor.value) {
        return false;
    }
    const next = nextStep(actedStep);
    if (next !== null) {
        navigate(next, { replace });
    }
    return true;
}

async function handleAdvance(completedStep: OnboardingStep, { replace = false }: { replace?: boolean } = {}): Promise<void> {
    if (advanceViewOnly(completedStep, { replace })) {
        return;
    }
    const cursorBefore = cursor.value;
    if (!(await advance(completedStep))) {
        return;
    }
    advanceViewAfter(completedStep, cursorBefore, { replace });
}

async function handleSkipCalendar(): Promise<void> {
    if (advanceViewOnly("calendar_connect")) {
        return;
    }
    const cursorBefore = cursor.value;
    if (!(await skipCalendar())) {
        return;
    }
    advanceViewAfter("calendar_connect", cursorBefore);
}

async function handleAssessmentComplete(): Promise<void> {
    const cursorBefore = cursor.value;
    if (!(await completeAssessment())) {
        return;
    }
    advanceViewAfter("assessment", cursorBefore);
}

async function handleSkipAssessment(): Promise<void> {
    if (advanceViewOnly("assessment")) {
        return;
    }
    const cursorBefore = cursor.value;
    if (!(await skipAssessment())) {
        return;
    }
    advanceViewAfter("assessment", cursorBefore);
}

async function handlePublish(access: AccessLevel): Promise<void> {
    if (await publishProfile(access)) {
        await handleComplete();
    }
}

async function handleComplete(): Promise<void> {
    const redirectUrl = await complete();
    if (redirectUrl !== null) {
        globalThis.location.href = redirectUrl;
    }
}

function handleRetry(): void {
    apiError.value = false;
    void enterView(step.value);
}

/* enterStep performs step work without navigating; this host decides how to handle empty results. */
async function enterView(current: OnboardingStep): Promise<void> {
    const outcome = await enterStep(current);
    if (outcome === "nothing_to_reveal") {
        await handleAdvance(current, { replace: true });
        return;
    }
    /*
     * Skipping the questions leaves profile generation without source material, so bypass preview and
     * publishing.
     */
    if (outcome === "nothing_to_publish") {
        await handleComplete();
    }
}

const flowBody = useTemplateRef<HTMLElement>("flowBody");

/*
 * Screen changes remove the focused control. Focus the new step region so keyboard navigation does not
 * restart at the page top.
 */
async function focusStepRegion(): Promise<void> {
    await nextTick();
    flowBody.value?.focus({ preventScroll: true });
}

watch(step, async (current) => {
    await focusStepRegion();
    await enterView(current);
});

/* Stats arriving is the signal to move to the payoff; the readings follow it. */
watch(
    () => scanPhase.outcome.value,
    (outcome) => {
        if (outcome === "resolved" && scanPhase.data.value?.status === "stats_ready") {
            void handleAdvance("scanning", { replace: true });
        }
    },
);

/*
 * Generation may finish without a document. Complete only while viewing the profile, so a late response
 * after Back cannot end the run.
 */
watch(profileSkipped, (skipped) => {
    if (skipped && step.value === "profile") {
        void handleComplete();
    }
});

onMounted(async () => {
    await enterView(step.value);
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .flow {
        @apply flex min-h-dvh flex-col bg-valence-page;
    }

    .flow-header {
        @apply flex items-start justify-center px-6 pt-6 sm:px-10;
    }

    .flow-body {
        @apply flex flex-1 flex-col;
    }
}
</style>
